import { useState, useCallback } from 'react';
import {
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  GitHub as GitHubIcon,
  OpenInNew as OpenInNewIcon,
  Star as StarIcon,
  MergeType as PullRequestIcon,
  AccountTree as BranchIcon,
  History as CommitIcon,
  PlayCircleOutline as WorkflowIcon,
  LocalOffer as TagIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Edit as EditIcon,
  VisibilityOff as VisibilityOffIcon,
  GroupAdd as GroupAddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
} from '@material-ui/icons';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import { Octokit } from '@octokit/rest';
import { HomepageRepo } from '../../types';
import { AiPromptMenu } from '../AiPromptMenu/AiPromptMenu';

// Client-side cache for repo details (1 minute TTL)
interface CachedRepoDetails {
  data: RepoDetails;
  expiresAt: number;
}
const repoDetailsCache = new Map<string, CachedRepoDetails>();
const REPO_DETAILS_CACHE_TTL = 60 * 1000; // 1 minute

const useStyles = makeStyles(theme => ({
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    minHeight: 56,
    '&.Mui-expanded': {
      minHeight: 56,
    },
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1),
  },
  repoName: {
    fontWeight: 500,
    flexGrow: 1,
  },
  chip: {
    height: 20,
    fontSize: '0.7rem',
  },
  sourceChip: {
    height: 18,
    fontSize: '0.65rem',
  },
  savedChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  autoChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  groupChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.5),
  },
  accordionDetails: {
    padding: 0,
    display: 'block',
  },
  section: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    paddingLeft: theme.spacing(2),
  },
  workflowRun: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(4),
    borderLeft: `3px solid ${theme.palette.divider}`,
    '&.success': {
      borderLeftColor: theme.palette.success.main,
    },
    '&.failure': {
      borderLeftColor: theme.palette.error.main,
    },
    '&.pending': {
      borderLeftColor: theme.palette.warning.main,
    },
  },
  lastActivity: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  commitInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  commitInfoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  branchChip: {
    height: 18,
    fontSize: '0.65rem',
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  tagChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    height: 20,
  },
  editButton: {
    padding: theme.spacing(0.5),
  },
  deleteMenuItem: {
    color: theme.palette.error.main,
  },
  confirmWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

interface RepoDetails {
  pullRequests: any[];
  branches: any[];
  commits: any[];
  workflowRuns: any[];
  tags: any[];
}

interface GitRepoListItemProps {
  repo: HomepageRepo;
  octokit: Octokit | null;
  editMode?: boolean;
  onHide?: (repo: HomepageRepo) => void;
  onEditGroups?: (repo: HomepageRepo) => void;
  onDelete?: (repo: HomepageRepo) => Promise<void>;
  userGroups?: string[];
}

export const GitRepoListItem = ({
  repo,
  octokit,
  editMode = false,
  onHide,
  onEditGroups,
  onDelete,
  userGroups = [],
}: GitRepoListItemProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<RepoDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formatRelativeDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatGroupName = (groupRef: string) => {
    if (groupRef.startsWith('user:')) {
      return 'Personal';
    }
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  const loadRepoDetails = useCallback(async () => {
    if (!octokit || details || loading) return;

    setLoading(true);
    setError(null);

    try {
      const [owner, name] = repo.fullName.split('/');
      const cacheKey = `${owner}/${name}`.toLowerCase();

      // Check cache first
      const cached = repoDetailsCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        setDetails(cached.data);
        setLoading(false);
        return;
      }

      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      const [prsResult, branchesResult, commitsResult, workflowsResult, tagsResult] = await Promise.all([
        octokit.pulls.list({ owner, repo: name, state: 'open', per_page: 10 }).catch(() => ({ data: [] })),
        octokit.repos.listBranches({ owner, repo: name, per_page: 20 }).catch(() => ({ data: [] })),
        octokit.repos.listCommits({ owner, repo: name, since: lastMonth.toISOString(), per_page: 10 }).catch(() => ({ data: [] })),
        octokit.actions.listWorkflowRunsForRepo({ owner, repo: name, per_page: 20 }).catch(() => ({ data: { workflow_runs: [] } })),
        octokit.repos.listTags({ owner, repo: name, per_page: 10 }).catch(() => ({ data: [] })),
      ]);

      const workflowRunsBySha = new Map<string, any[]>();
      for (const run of workflowsResult.data.workflow_runs || []) {
        const runs = workflowRunsBySha.get(run.head_sha) || [];
        runs.push(run);
        workflowRunsBySha.set(run.head_sha, runs);
      }

      const tagsBySha = new Map<string, any[]>();
      for (const tag of tagsResult.data) {
        const tags = tagsBySha.get(tag.commit.sha) || [];
        tags.push(tag);
        tagsBySha.set(tag.commit.sha, tags);
      }

      const enhancedCommits = commitsResult.data.map((commit: any) => ({
        sha: commit.sha.substring(0, 7),
        fullSha: commit.sha,
        message: commit.commit.message.split('\n')[0],
        author: {
          login: commit.author?.login,
          name: commit.commit.author?.name || 'Unknown',
          date: commit.commit.author?.date,
        },
        html_url: commit.html_url,
        workflowRuns: workflowRunsBySha.get(commit.sha) || [],
        tags: tagsBySha.get(commit.sha) || [],
      }));

      const repoDetails: RepoDetails = {
        pullRequests: prsResult.data,
        branches: branchesResult.data,
        commits: enhancedCommits,
        workflowRuns: workflowsResult.data.workflow_runs || [],
        tags: tagsResult.data,
      };

      // Cache the result
      repoDetailsCache.set(cacheKey, {
        data: repoDetails,
        expiresAt: Date.now() + REPO_DETAILS_CACHE_TTL,
      });

      setDetails(repoDetails);
    } catch (err: any) {
      setError(err.message || 'Failed to load repository details');
    } finally {
      setLoading(false);
    }
  }, [octokit, repo.fullName, details, loading]);

  const handleExpand = useCallback(
    (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      if (editMode) return; // Don't expand in edit mode
      setExpanded(isExpanded);
      if (isExpanded && !details && !loading) {
        loadRepoDetails();
      }
    },
    [editMode, details, loading, loadRepoDetails],
  );

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleHide = useCallback(() => {
    handleMenuClose();
    onHide?.(repo);
  }, [onHide, repo, handleMenuClose]);

  const handleEditGroups = useCallback(() => {
    handleMenuClose();
    onEditGroups?.(repo);
  }, [onEditGroups, repo, handleMenuClose]);

  const handleDeleteClick = useCallback(() => {
    handleMenuClose();
    setConfirmDeleteOpen(true);
  }, [handleMenuClose]);

  const handleConfirmDelete = useCallback(async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(repo);
      setConfirmDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [onDelete, repo]);

  const getWorkflowStatusIcon = (run: any) => {
    if (run.status === 'completed') {
      if (run.conclusion === 'success') {
        return <CheckCircleIcon style={{ color: 'green', fontSize: 16 }} />;
      }
      return <ErrorIcon style={{ color: 'red', fontSize: 16 }} />;
    }
    return <PendingIcon style={{ color: 'orange', fontSize: 16 }} />;
  };

  const getWorkflowStatusClass = (run: any) => {
    if (run.status === 'completed') {
      return run.conclusion === 'success' ? 'success' : 'failure';
    }
    return 'pending';
  };

  // Get groups this repo belongs to that the user is a member of
  const repoGroups = repo.groups?.filter(g => userGroups.includes(g) || g.startsWith('user:')) || [];

  return (
    <>
      <Accordion
        className={classes.accordion}
        expanded={editMode ? false : expanded}
        onChange={handleExpand}
      >
        <AccordionSummary
          expandIcon={
            editMode ? (
              <Tooltip title="Edit repository">
                <IconButton
                  size="small"
                  className={classes.editButton}
                  onClick={handleEditClick}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <ExpandMoreIcon />
            )
          }
          className={classes.accordionSummary}
        >
          <Box className={classes.summaryContent}>
            <GitHubIcon fontSize="small" />
            <Typography className={classes.repoName}>{repo.fullName}</Typography>
            {repo.isFromActivity ? (
              <Chip
                label="Auto"
                size="small"
                className={`${classes.sourceChip} ${classes.autoChip}`}
              />
            ) : (
              <Chip
                label="Saved"
                size="small"
                className={`${classes.sourceChip} ${classes.savedChip}`}
              />
            )}
            {/* Show group chips for repos with team associations */}
            {repoGroups.map(groupRef => (
              <Chip
                key={groupRef}
                label={formatGroupName(groupRef)}
                size="small"
                className={classes.groupChip}
                variant="outlined"
              />
            ))}
            {repo.language && (
              <Chip label={repo.language} size="small" className={classes.chip} variant="outlined" />
            )}
            {repo.stargazersCount !== undefined && repo.stargazersCount > 0 && (
              <Chip
                icon={<StarIcon style={{ fontSize: 14 }} />}
                label={repo.stargazersCount}
                size="small"
                className={classes.chip}
                variant="outlined"
              />
            )}
            {repo.lastCommitBranch && (
              <Chip
                icon={<BranchIcon style={{ fontSize: 12 }} />}
                label={repo.lastCommitBranch}
                size="small"
                className={classes.branchChip}
                component="a"
                href={`https://github.com/${repo.fullName}/tree/${repo.lastCommitBranch}`}
                target="_blank"
                clickable
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            )}
            {repo.lastCommitAuthor && (
              <Box className={classes.commitInfoItem}>
                <PersonIcon style={{ fontSize: 14 }} />
                <Typography variant="caption">{repo.lastCommitAuthor}</Typography>
              </Box>
            )}
            {repo.lastActivity && (
              <Tooltip title={new Date(repo.lastActivity).toLocaleString()}>
                <Typography className={classes.lastActivity}>
                  {formatRelativeDate(repo.lastActivity)}
                </Typography>
              </Tooltip>
            )}
            <Tooltip title="Open in GitHub">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  window.open(repo.repoUrl, '_blank');
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <AiPromptMenu
              repository={repo.fullName}
              label="repo"
            />
          </Box>
        </AccordionSummary>
        {!editMode && (
          <AccordionDetails className={classes.accordionDetails}>
            {loading && (
              <Box className={classes.loadingState}>
                <CircularProgress size={24} />
              </Box>
            )}
            {error && (
              <Box className={classes.section}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
            {details && (
              <>
                {/* Pull Requests Section */}
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    <PullRequestIcon fontSize="small" />
                    Open Pull Requests ({details.pullRequests.length})
                  </Typography>
                  {details.pullRequests.length === 0 ? (
                    <Typography className={classes.emptyState}>No open pull requests</Typography>
                  ) : (
                    <List dense disablePadding>
                      {details.pullRequests.map((pr: any) => (
                        <ListItem
                          key={pr.id}
                          className={classes.listItem}
                          button
                          component="a"
                          href={pr.html_url}
                          target="_blank"
                        >
                          <ListItemIcon>
                            <PullRequestIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`#${pr.number} ${pr.title}`}
                            secondary={`by ${pr.user?.login || 'unknown'} - ${formatRelativeDate(pr.updated_at)}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {/* Branches Section */}
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    <BranchIcon fontSize="small" />
                    Active Branches ({details.branches.length})
                  </Typography>
                  {details.branches.length === 0 ? (
                    <Typography className={classes.emptyState}>No branches found</Typography>
                  ) : (
                    <List dense disablePadding>
                      {details.branches.slice(0, 10).map((branch: any) => (
                        <ListItem key={branch.name} className={classes.listItem}>
                          <ListItemIcon>
                            <BranchIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={branch.name}
                            secondary={branch.protected ? 'Protected' : undefined}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {/* Recent Commits Section */}
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    <CommitIcon fontSize="small" />
                    Recent Commits ({details.commits.length})
                  </Typography>
                  {details.commits.length === 0 ? (
                    <Typography className={classes.emptyState}>No recent commits</Typography>
                  ) : (
                    <List dense disablePadding>
                      {details.commits.map((commit: any) => (
                        <ListItem
                          key={commit.fullSha}
                          className={classes.listItem}
                          button
                          component="a"
                          href={commit.html_url}
                          target="_blank"
                        >
                          <ListItemIcon>
                            <CommitIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <code>{commit.sha}</code>
                                <span>{commit.message}</span>
                                {commit.tags?.map((tag: any) => (
                                  <Chip
                                    key={tag.name}
                                    icon={<TagIcon style={{ fontSize: 12 }} />}
                                    label={tag.name}
                                    size="small"
                                    className={classes.tagChip}
                                  />
                                ))}
                              </Box>
                            }
                            secondary={`by ${commit.author.login || commit.author.name} - ${formatRelativeDate(commit.author.date)}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {/* Workflow Runs Section */}
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    <WorkflowIcon fontSize="small" />
                    Workflow Runs ({details.workflowRuns.length})
                  </Typography>
                  {details.workflowRuns.length === 0 ? (
                    <Typography className={classes.emptyState}>No workflow runs</Typography>
                  ) : (
                    <List dense disablePadding>
                      {details.workflowRuns.slice(0, 10).map((run: any) => (
                        <ListItem
                          key={run.id}
                          className={`${classes.workflowRun} ${getWorkflowStatusClass(run)}`}
                          button
                          component="a"
                          href={run.html_url}
                          target="_blank"
                        >
                          <ListItemIcon>{getWorkflowStatusIcon(run)}</ListItemIcon>
                          <ListItemText
                            primary={`${run.name} #${run.run_number}`}
                            secondary={`${run.event} - ${formatRelativeDate(run.created_at)}`}
                          />
                          <AiPromptMenu
                            repository={repo.fullName}
                            branch={run.head_branch}
                            label="workflow"
                            githubRunId={String(run.id)}
                            githubSha={run.head_sha}
                            githubRunNumber={String(run.run_number)}
                            githubEvent={run.event}
                            githubJobRef={run.path}
                            githubWorkflowRef={`${repo.fullName}/${run.path}@${run.head_branch}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {/* Tags Section */}
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    <TagIcon fontSize="small" />
                    Tags ({details.tags.length})
                  </Typography>
                  {details.tags.length === 0 ? (
                    <Typography className={classes.emptyState}>No tags</Typography>
                  ) : (
                    <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                      {details.tags.map((tag: any) => (
                        <Chip
                          key={tag.name}
                          icon={<TagIcon style={{ fontSize: 12 }} />}
                          label={tag.name}
                          size="small"
                          className={classes.tagChip}
                          component="a"
                          href={`https://github.com/${repo.fullName}/releases/tag/${tag.name}`}
                          target="_blank"
                          clickable
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </AccordionDetails>
        )}
      </Accordion>

      {/* Edit Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditGroups}>
          <GroupAddIcon fontSize="small" style={{ marginRight: 8 }} />
          {repo.isFromActivity ? 'Save to list' : 'Edit groups'}
        </MenuItem>
        <MenuItem onClick={handleHide}>
          <VisibilityOffIcon fontSize="small" style={{ marginRight: 8 }} />
          Hide repository
        </MenuItem>
        {/* Only show delete for saved repos */}
        {!repo.isFromActivity && onDelete && (
          <MenuItem onClick={handleDeleteClick} className={classes.deleteMenuItem}>
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
            Delete repository
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !deleting && setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Box className={classes.confirmWarning}>
            <WarningIcon />
            <Typography>This action cannot be undone.</Typography>
          </Box>
          <Typography>
            Are you sure you want to delete "<strong>{repo.fullName}</strong>"?
            This will remove it from all groups.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            className={classes.deleteButton}
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
